import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLang } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function Login({ mode = 'login' }) {
  const isRegister = mode === 'register';
  const { t } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', company: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) await register(form);
      else await login(form.email, form.password);
      navigate('/portal');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('auth.invalid'));
    } finally { setLoading(false); }
  };

  return (
    <div className="itw-noise min-h-screen gradient-hero grid place-items-center px-4 py-24">
      <div className="absolute inset-0 itw-grid opacity-40" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-cyan)] transition-colors mb-6"><ArrowLeft className="h-4 w-4" /> IT-WELT360</Link>
        <div className="glass rounded-2xl p-8 neon-edge">
          <h1 className="font-display text-2xl font-semibold text-[color:var(--itw-text)]">{isRegister ? t('auth.registerTitle') : t('auth.loginTitle')}</h1>
          <p className="mt-2 text-sm text-[color:var(--itw-text-2)]">{isRegister ? t('auth.registerSubtitle') : t('auth.loginSubtitle')}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {isRegister && (
              <>
                <div className="space-y-1.5"><Label>{t('auth.name')}</Label><Input value={form.name} onChange={update('name')} required data-testid="register-name-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                <div className="space-y-1.5"><Label>{t('auth.company')}</Label><Input value={form.company} onChange={update('company')} data-testid="register-company-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
              </>
            )}
            <div className="space-y-1.5"><Label>{t('auth.email')}</Label><Input type="email" value={form.email} onChange={update('email')} required data-testid="login-email-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
            <div className="space-y-1.5"><Label>{t('auth.password')}</Label><Input type="password" value={form.password} onChange={update('password')} required data-testid="login-password-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('auth.loggingIn')}</> : (isRegister ? t('auth.registerBtn') : t('auth.loginBtn'))}
            </Button>
          </form>
          <div className="mt-5 text-center text-sm text-[color:var(--itw-text-2)]">
            {isRegister ? t('auth.hasAccount') : t('auth.noAccount')}{' '}
            <Link to={isRegister ? '/login' : '/register'} className="text-[color:var(--itw-cyan)] hover:underline" data-testid="auth-switch-link">
              {isRegister ? t('auth.toLogin') : t('auth.toRegister')}
            </Link>
          </div>
          {!isRegister && (
            <div className="mt-4 text-center text-xs text-[color:var(--itw-text-3)] glass rounded-lg p-2 border border-[color:var(--itw-border)]">{t('auth.demoHint')}</div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
